// Added by Sejune Oh on 2019-03-29
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { action, makeObservable, observable } from 'mobx'

import Rootstore from '../index'
import DataStorageStoreBase from '../base/DataStorageStoreBase'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
export interface DSStudyElementBaseProps {
  id?: string
  fk_patient_id?: string
  fk_study_folder_id?: string
  patient_id?: string
  patient_name?: string
  patient_sex?: string
  patient_birth_date?: string
  study_instance_uid?: string
  study_id?: string
  accession_number?: string
  study_description?: string
  patient_age?: string
  other_patient_ids?: string
  study_date_time?: string
  series_folder_count?: number
  series_count?: number
  sop_instance_count?: number
  locked_by?: string
  locked_at?: string
  created_at?: string
  updated_at?: string
  deleted_at?: string
  group_key?: string
}

export interface DSStudyElementProps extends DSStudyElementBaseProps, ChangeStudyStatusArgParams {}

interface ChangeStudyStatusArgParams {
  src_status_id?: string
  dst_status_id?: string
  username?: string
  user_email?: string
  user_display_name?: string
  proxy_token?: string
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export default class DataStorageStudyStore extends DataStorageStoreBase<DSStudyElementProps> {
  totalCount: number = 0
  elements: Array<DSStudyElementProps> = []
  root?: Rootstore

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  constructor(root: Rootstore) {
    super({ storeName: 'DS_StudyStore', apiSubUrl: 'dcm/studies', _root: root })
    this.root = root
    makeObservable(this, {
      totalCount: observable,
      elements: observable,
      _setTotalCount: action,
      setElements: action,
    })
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  getElement = async (id: string) => {
    return await super.get(id)
  }

  getElements = async (path: string) => {
    const rtnData = await super.getAll(path)
    await this._setTotalCount(parseInt(rtnData.total_count as string))
    return rtnData.items as Array<DSStudyElementProps>
  }

  addElement = async (element: DSStudyElementProps) => {
    const newElement = await super.add(element)
    return newElement.id
  }

  removeElement = async (id: string) => {
    return await super.remove(id)
  }

  updateElement = async (id: string, element: DSStudyElementProps) => {
    const updatedElement = await super.update(id, element)
    return updatedElement.id
  }

  changeStudyStatus = async (props: ChangeStudyStatusArgParams) => {
    return await super.postCustom({
      subUrl: ':changestatus',
      element: {
        ...props,
      },
    })
  }

  getTotalStudyCount = async () => {
    const rtnData = await super.getAll('')
    return rtnData.total_count
  }

  getTotalStudyCountByStudyStatus = async (studyStatusIds: Array<string>) => {
    const rtnData = await super.getAll(`fk_study_status_ids=${studyStatusIds.join(',')}`)
    return rtnData.total_count
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _setTotalCount = (count: number) => {
    this.totalCount = count
  }

  setElements = (elements: Array<DSStudyElementProps>) => {
    this.elements = elements
  }
}
